import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import './style.css';
import BlockTypes, { MUTABLE_BLOCKS } from '../../types/BlockTypes';
import StyleTypes from '../../types/StyleTypes';
import { normalizeNode, shouldBeAtRoot, shouldNotBeEmpty, shouldMergeWithSimilarSiblings, shouldHaveChildrenOfType } from '../utils/NormalizationHelper';
import renderBlocks from '../utils/renderBlocks';
import renderMarks from '../utils/renderMarks';
import UnorderedListItem from './components/UnorderedListItem';
import OrderedListItem from './components/OrderedListItem';
import Unstyled from './components/Unstyled';
import { BoldIcon, ItalicIcon, Heading1Icon, Heading2Icon, Heading3Icon, StrikethroughIcon, SubscriptIcon, SuperscriptIcon, UnderlineIcon, OrderedListIcon, BulletListIcon, CodeIcon } from '../../components/Icons';
import makeKeyboardInlineShortcut from '../utils/makeKeyboardInlineShortcut';
import makeKeyboardBlockShortcut from '../utils/makeKeyboardBlockShortcut';
import makeMarkdownBlockShortcut from '../utils/makeMarkdownBlockShortcut';
import makeMarkdownInlineShortcut from '../utils/makeMarkdownInlineShortcut';
import makeToolbarButton, { BASIC_SELECTION, EXPANDED_SELECTION, BASIC_INSERTION, EXPANDED_INSERTION } from '../utils/makeToolbarButton';
import serializers from './serializers';
import makeTypingReplacementShortcut from '../utils/makeTypingReplacementShortcut';
export default (function () {
  var _renderMarks, _renderBlocks;

  return [normalizeNode([BlockTypes.UNSTYLED, BlockTypes.H1, BlockTypes.H2, BlockTypes.H3, BlockTypes.UL, BlockTypes.OL], [shouldBeAtRoot]), normalizeNode([BlockTypes.UL, BlockTypes.OL], [shouldNotBeEmpty, shouldMergeWithSimilarSiblings]), normalizeNode(BlockTypes.UL, [[shouldHaveChildrenOfType, BlockTypes.UL_LI]]), normalizeNode(BlockTypes.OL, [[shouldHaveChildrenOfType, BlockTypes.OL_LI]]), {
    serializers: serializers
  }, renderMarks((_renderMarks = {}, _defineProperty(_renderMarks, StyleTypes.BOLD, 'b'), _defineProperty(_renderMarks, StyleTypes.CODE, 'code'), _defineProperty(_renderMarks, StyleTypes.ITALIC, 'i'), _defineProperty(_renderMarks, StyleTypes.STRIKETHROUGH, 'del'), _defineProperty(_renderMarks, StyleTypes.SUBSCRIPT, 'sub'), _defineProperty(_renderMarks, StyleTypes.SUPERSCRIPT, 'sup'), _defineProperty(_renderMarks, StyleTypes.UNDERLINE, 'u'), _renderMarks)), renderBlocks((_renderBlocks = {}, _defineProperty(_renderBlocks, BlockTypes.H1, 'h1'), _defineProperty(_renderBlocks, BlockTypes.H2, 'h2'), _defineProperty(_renderBlocks, BlockTypes.H3, 'h3'), _defineProperty(_renderBlocks, BlockTypes.UL, function (props) {
    return props.children;
  }), _defineProperty(_renderBlocks, BlockTypes.UL_LI, function (props) {
    return React.createElement(UnorderedListItem, props);
  }), _defineProperty(_renderBlocks, BlockTypes.OL, function (props) {
    return props.children;
  }), _defineProperty(_renderBlocks, BlockTypes.OL_LI, function (props) {
    return React.createElement(OrderedListItem, props);
  }), _defineProperty(_renderBlocks, BlockTypes.UNSTYLED, function (props) {
    return React.createElement(Unstyled, props);
  }), _renderBlocks)), makeToolbarButton(StyleTypes.BOLD, {
    icon: BoldIcon,
    title: 'Bold',
    visibility: BASIC_SELECTION | EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.BOLD);
    },
    onApply: function onApply(editor) {
      return editor.toggleMark(StyleTypes.BOLD);
    }
  }), makeToolbarButton(StyleTypes.ITALIC, {
    icon: ItalicIcon,
    title: 'Italic',
    visibility: BASIC_SELECTION | EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.ITALIC);
    },
    onApply: function onApply(editor) {
      return editor.toggleMark(StyleTypes.ITALIC);
    }
  }), makeToolbarButton(BlockTypes.H1, {
    icon: Heading1Icon,
    title: 'Heading 1',
    alias: ['title 1', 'h1'],
    visibility: BASIC_SELECTION | EXPANDED_SELECTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    isActive: function isActive(editor) {
      return editor.isActiveBlock(BlockTypes.H1);
    },
    onApply: function onApply(editor) {
      return editor.toggleBlock(BlockTypes.H1);
    }
  }), makeToolbarButton(BlockTypes.H2, {
    icon: Heading2Icon,
    title: 'Heading 2',
    alias: ['title 2', 'h2', 'subtitle'],
    visibility: BASIC_SELECTION | EXPANDED_SELECTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    isActive: function isActive(editor) {
      return editor.isActiveBlock(BlockTypes.H2);
    },
    onApply: function onApply(editor) {
      return editor.toggleBlock(BlockTypes.H2);
    }
  }), makeToolbarButton(BlockTypes.H3, {
    icon: Heading3Icon,
    title: 'Heading 3',
    alias: ['title 3', 'h3', 'subtitle'],
    visibility: EXPANDED_SELECTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    isActive: function isActive(editor) {
      return editor.isActiveBlock(BlockTypes.H3);
    },
    onApply: function onApply(editor) {
      return editor.toggleBlock(BlockTypes.H3);
    }
  }), makeToolbarButton(StyleTypes.STRIKETHROUGH, {
    icon: StrikethroughIcon,
    title: 'Strikethrough',
    visibility: EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.STRIKETHROUGH);
    },
    onApply: function onApply(editor) {
      return editor.toggleMark(StyleTypes.STRIKETHROUGH);
    }
  }), makeToolbarButton(StyleTypes.SUBSCRIPT, {
    icon: SubscriptIcon,
    title: 'Subscript',
    visibility: EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.SUBSCRIPT);
    },
    onApply: function onApply(editor) {
      return editor.removeMark(StyleTypes.SUPERSCRIPT).toggleMark(StyleTypes.SUBSCRIPT);
    }
  }), makeToolbarButton(StyleTypes.SUPERSCRIPT, {
    icon: SuperscriptIcon,
    title: 'Superscript',
    visibility: EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.SUPERSCRIPT);
    },
    onApply: function onApply(editor) {
      return editor.removeMark(StyleTypes.SUBSCRIPT).toggleMark(StyleTypes.SUPERSCRIPT);
    }
  }), makeToolbarButton(StyleTypes.UNDERLINE, {
    icon: UnderlineIcon,
    title: 'Underline',
    visibility: EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.UNDERLINE);
    },
    onApply: function onApply(editor) {
      return editor.toggleMark(StyleTypes.UNDERLINE);
    }
  }), makeToolbarButton(BlockTypes.OL_LI, {
    icon: OrderedListIcon,
    title: 'Numbered list',
    alias: ['ordered list', 'ol'],
    visibility: BASIC_SELECTION | EXPANDED_SELECTION | BASIC_INSERTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    isActive: function isActive(editor) {
      return editor.isActiveBlock(BlockTypes.OL_LI);
    },
    onApply: function onApply(editor) {
      return editor.toggleBlock(BlockTypes.OL_LI);
    }
  }), makeToolbarButton(BlockTypes.UL_LI, {
    icon: BulletListIcon,
    title: 'Bullet list',
    alias: ['unordered list', 'ul'],
    visibility: EXPANDED_SELECTION | BASIC_INSERTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    isActive: function isActive(editor) {
      return editor.isActiveBlock(BlockTypes.UL_LI);
    },
    onApply: function onApply(editor) {
      return editor.toggleBlock(BlockTypes.UL_LI);
    }
  }), makeToolbarButton(StyleTypes.CODE, {
    icon: CodeIcon,
    title: 'Inline code',
    visibility: BASIC_SELECTION | EXPANDED_SELECTION,
    isActive: function isActive(editor) {
      return editor.isActiveMark(StyleTypes.CODE);
    },
    onApply: function onApply(editor) {
      return editor.toggleMark(StyleTypes.CODE);
    }
  }), makeKeyboardInlineShortcut('mod+i', StyleTypes.ITALIC), makeKeyboardInlineShortcut('mod+b', StyleTypes.BOLD), makeKeyboardInlineShortcut('mod+u', StyleTypes.UNDERLINE), makeKeyboardInlineShortcut('mod+j', StyleTypes.CODE), makeKeyboardInlineShortcut('mod+shift+s', StyleTypes.STRIKETHROUGH), makeKeyboardInlineShortcut('mod+,', function (editor) {
    return editor.removeMark(StyleTypes.SUPERSCRIPT).toggleMark(StyleTypes.SUBSCRIPT);
  }), makeKeyboardInlineShortcut('mod+.', function (editor) {
    return editor.removeMark(StyleTypes.SUBSCRIPT).toggleMark(StyleTypes.SUPERSCRIPT);
  }), makeMarkdownInlineShortcut(/(\*.+\*)$/, /\*(.+)\*/, StyleTypes.BOLD), makeMarkdownInlineShortcut(/(_.+_)$/, /_(.+)_/, StyleTypes.ITALIC), makeMarkdownInlineShortcut(/(~.+~)$/, /~(.+)~/, StyleTypes.STRIKETHROUGH), makeMarkdownInlineShortcut(/(`.+)$/, /`(.+)/, StyleTypes.CODE, {
    trigger: '`'
  }), makeMarkdownInlineShortcut(/(`.+`)$/, /`(.+)`/, StyleTypes.CODE), makeKeyboardBlockShortcut('mod+shift+1', BlockTypes.H1), makeKeyboardBlockShortcut('mod+shift+2', BlockTypes.H2), makeKeyboardBlockShortcut('mod+alt+3', BlockTypes.H3), makeKeyboardBlockShortcut('mod+shift+7', BlockTypes.OL_LI), makeKeyboardBlockShortcut('mod+shift+8', BlockTypes.UL_LI), makeKeyboardBlockShortcut('mod+shift+c', BlockTypes.TODO), makeMarkdownBlockShortcut(/^(-|\*)$/, BlockTypes.UL_LI), makeMarkdownBlockShortcut(/^(1\.)$/, BlockTypes.OL_LI), makeMarkdownBlockShortcut(/^(#)$/, BlockTypes.H1, {
    onlyOnBlocksOfTypes: MUTABLE_BLOCKS
  }), makeMarkdownBlockShortcut(/^(##)$/, BlockTypes.H2, {
    onlyOnBlocksOfTypes: MUTABLE_BLOCKS
  }), makeMarkdownBlockShortcut(/^(###)$/, BlockTypes.H3, {
    onlyOnBlocksOfTypes: MUTABLE_BLOCKS
  }), makeTypingReplacementShortcut(/(?:^|\s)\d+(st|nd|rd|th)$/, function (editor, event, matches) {
    return editor.insertText(matches.before[1], [StyleTypes.SUPERSCRIPT]).insertText(' ', []);
  })];
});